<template>
    <Toast />
    <ConfirmDialog />
    <div class="grid">
        <div class="col-12 lg:col-8">
            <div class="card">
                <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-3">
                    <div>
                        <h5 class="m-0">Sincronizar Stock</h5>
                        <small class="text-color-secondary">Lleva el stock de un producto a lo que hay físicamente. Queda registrado quién, cuándo y cuánto se ajustó.</small>
                    </div>
                    <Button v-if="isManager" label="Ajustes de inventario" icon="pi pi-chart-line" class="p-button-text mt-2 md:mt-0" @click="$router.push('/dashboard/inventario/ajustes')" />
                </div>

                <!-- Formulario -->
                <div v-if="!result">
                    <!-- Solo para quien no gestiona el inventario: el admin sincroniza cualquier producto con stock. -->
                    <Message v-if="noProducts && !isManager" severity="info" :closable="false">
                        Solicitar permiso para sincronizar productos al Administrador.
                    </Message>

                    <div class="field">
                        <label for="product" class="font-medium block">Producto</label>
                        <AutoComplete
                            id="product"
                            v-model="product"
                            :suggestions="suggestions"
                            field="name"
                            :dropdown="true"
                            forceSelection
                            placeholder="Buscar producto..."
                            class="w-full"
                            inputClass="w-full"
                            @complete="search($event.query)"
                        >
                            <template #item="{ item }">
                                <div>{{ item.name }}</div>
                                <small class="text-color-secondary">{{ item.code || '' }} {{ item.categoryName ? '· ' + item.categoryName : '' }}</small>
                            </template>
                        </AutoComplete>
                    </div>

                    <div class="field">
                        <label for="physical" class="font-medium block">Stock físico actual</label>
                        <InputNumber
                            id="physical"
                            v-model="physicalStock"
                            mode="decimal"
                            :min="0"
                            :minFractionDigits="0"
                            :maxFractionDigits="3"
                            locale="en-US"
                            :useGrouping="false"
                            placeholder="Lo que hay ahora"
                            class="w-full md:w-15rem"
                        />
                        <small class="block text-color-secondary mt-1">
                            Cuente todo lo que hay del producto en este momento, también lo que está en bodega. Por ejemplo, al
                            vaciarse un barril: los barriles llenos que quedan × sus litros. Sincronice antes de conectar el siguiente.
                        </small>
                    </div>

                    <div class="field">
                        <label class="font-medium block">Motivo</label>
                        <SelectButton v-model="reasonCode" :options="reasons" optionLabel="label" optionValue="value" />
                    </div>

                    <div class="field">
                        <label for="reason" class="font-medium block">Detalle (opcional)</label>
                        <Textarea id="reason" v-model.trim="reason" rows="2" class="w-full" placeholder="Ej.: barril vacío, espuma y limpieza de líneas" />
                    </div>

                    <div class="flex justify-content-end">
                        <Button label="Sincronizar" icon="pi pi-sync" class="p-button-success" :loading="saving" :disabled="!canSave" @click="confirmSave" />
                    </div>
                </div>

                <!-- Resultado (desde la respuesta del servidor) -->
                <div v-if="result">
                    <div class="text-center my-3">
                        <i class="pi pi-check-circle text-green-500" style="font-size: 3rem"></i>
                        <h4 class="mt-2 mb-1">{{ result.productName }}</h4>
                        <Tag v-if="isConsumption" value="Consumo" severity="info" />
                        <Tag v-else :value="resultLabel(result.result)" :severity="resultSeverity(result.result)" />
                    </div>
                    <div class="grid text-center">
                        <div class="col-12 md:col-4"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">El sistema tenía</div><div class="font-bold text-xl">{{ formatQty(result.previousStock) }}</div></div></div>
                        <div class="col-12 md:col-4"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Quedó en</div><div class="font-bold text-xl">{{ formatQty(result.newStock) }}</div></div></div>
                        <div class="col-12 md:col-4"><div class="surface-100 border-round p-3"><div class="text-sm text-color-secondary">Diferencia</div><div class="font-bold text-xl" :class="diffClass(result.difference)">{{ formatSignedQty(result.difference) }}</div></div></div>
                    </div>
                    <div v-if="isManager && result.value !== undefined" class="surface-100 border-round p-3 mt-3 text-center">
                        <div class="text-sm text-color-secondary">{{ valueLabel }}</div>
                        <!-- El consumo se lee en positivo: es lo que se usó, no una pérdida. -->
                        <div v-if="isConsumption" class="font-bold text-xl">{{ formatMoney(-Number(result.value)) }}</div>
                        <div v-else class="font-bold text-xl" :class="diffClass(result.value)">{{ formatMoney(result.value) }}</div>
                        <small v-if="result.valueUncertain" class="text-color-secondary">Sin compras con que valorarlo.</small>
                    </div>
                    <Message v-if="result.needsReview" severity="warn" :closable="false" class="mt-3">
                        El sistema tenía el stock en negativo: se vendió más de lo que había registrado. Si falta registrar una
                        compra (por ejemplo, el barril que se conectó), regístrela: el administrador revisará este ajuste.
                    </Message>
                    <div class="flex justify-content-center gap-2 mt-4">
                        <Button label="Sincronizar otro" icon="pi pi-refresh" class="p-button-outlined" @click="reset" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';
import {
    defaultSyncReason, diffClass, formatMoney, formatQty, formatSignedQty, isValidPhysical, reasonsFor,
    resultLabel, resultSeverity,
} from './inventoryFormat';

export default {
    name: 'StockSync',
    data() {
        return {
            product: null,
            suggestions: [],
            noProducts: false,
            physicalStock: null,
            reasonCode: defaultSyncReason(false),
            reason: '',
            saving: false,
            result: null,
        };
    },
    async created() {
        // `null` es una carga fallida, no una lista vacía: el aviso solo sale si el
        // servidor respondió que no hay productos habilitados.
        const products = await this.fetch(null);
        this.noProducts = Array.isArray(products) && products.length === 0;
        const productId = this.$route.query.productId;
        if (productId) {
            const match = (await this.fetch(null, productId)) || [];
            this.product = match[0] || null;
        }
    },
    computed: {
        // "Consumo normal" solo para un insumo periódico (sal, limpieza).
        periodic() {
            return !!(this.product && this.product.periodic);
        },
        reasons() {
            return reasonsFor(this.periodic);
        },
        isManager() {
            return this.$can('inventoryManage');
        },
        valueLabel() {
            if (this.isConsumption) return 'Consumo registrado';
            return this.result && Number(this.result.value) < 0 ? 'Pérdida registrada' : 'Ganancia registrada';
        },
        isConsumption() {
            return !!(this.result && this.result.reasonCode === 'CONSUMO');
        },
        canSave() {
            return this.product && this.product.productId && isValidPhysical(this.physicalStock)
                && this.reasonCode;
        },
    },
    methods: {
        diffClass, formatMoney, formatQty, formatSignedQty, resultLabel, resultSeverity,
        async fetch(filter, productId) {
            const r = await httpRequest.getSyncableProducts(filter, productId);
            if (r.status === 200 && Array.isArray(r.data)) return r.data;
            this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudieron cargar los productos', life: 4000 });
            return null;
        },
        async search(query) {
            this.suggestions = (await this.fetch(query || null)) || [];
        },
        confirmSave() {
            this.$confirm.require({
                message: `El stock de «${this.product.name}» pasará a ${formatQty(this.physicalStock)}. La diferencia quedará registrada. ¿Continuar?`,
                header: 'Confirmar sincronización',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Sincronizar',
                rejectLabel: 'Cancelar',
                accept: () => this.save(),
            });
        },
        async save() {
            this.saving = true;
            try {
                const r = await httpRequest.saveStockSync(this.product.productId, String(this.physicalStock), this.reason, this.reasonCode);
                if (r.status === 200 && r.data) {
                    this.result = r.data;
                } else {
                    this.$toast.add({ severity: 'error', summary: 'No se sincronizó', detail: r.error || 'Intente de nuevo', life: 5000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.saving = false;
            }
        },
        reset() {
            this.result = null;
            this.product = null;
            this.physicalStock = null;
            this.reason = '';
            this.reasonCode = defaultSyncReason(false);
        },
    },
    watch: {
        periodic(value) {
            this.reasonCode = defaultSyncReason(value);
        },
    },
};
</script>
